import { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import useAuthStore from '../stores/authStore';
import useLang from '../hooks/useLang';

const fmt  = (v) => new Intl.NumberFormat('sw-TZ').format(v || 0);
const fmtK = (v) => {
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M';
  if (v >= 1_000)     return (v / 1_000).toFixed(0) + 'k';
  return String(v || 0);
};

const SIKU    = ['Ju', 'At', 'Ju', 'Al', 'Ij', 'Si', 'Sa']; // Mon-Sun short
const MIEZI   = ['Januari','Februari','Machi','Aprili','Mei','Juni',
                 'Julai','Agosti','Septemba','Oktoba','Novemba','Desemba'];

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}
// Day of week for first day (0=Mon … 6=Sun)
function firstDayOffset(year, month) {
  const d = new Date(year, month - 1, 1).getDay();
  return (d + 6) % 7; // convert Sun=0 → Mon-first
}

// Heat colour based on fraction 0-1
function heatColor(fraction) {
  if (fraction === 0)     return 'bg-gray-50 text-gray-400 border-gray-100';
  if (fraction < 0.25)    return 'bg-green-100 text-green-700 border-green-200';
  if (fraction < 0.50)    return 'bg-green-300 text-green-900 border-green-400';
  if (fraction < 0.75)    return 'bg-green-500 text-white border-green-600';
  return 'bg-green-700 text-white border-green-800';
}

export default function Calendar() {
  const { T } = useLang();
  const { user } = useAuthStore();
  const now = new Date();

  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data,  setData]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups]   = useState([]);
  const [groupId, setGroupId] = useState(user?.group_id || '');
  const [selectedDay, setSelectedDay] = useState(null);       // 'YYYY-MM-DD'
  const [dayTxns, setDayTxns]         = useState([]);
  const [dayLoading, setDayLoading]   = useState(false);

  // Load groups for superadmin
  useEffect(() => {
    if (user?.role === 'superadmin') {
      api.get('/groups').then(r => setGroups(r.data));
    }
  }, [user]);

  const load = useCallback(() => {
    if (!groupId) { setLoading(false); return; }
    setLoading(true);
    api.get(`/calendar/daily-totals?group_id=${groupId}&year=${year}&month=${month}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [groupId, year, month]);

  useEffect(() => { load(); }, [load]);

  const openDay = async (dateStr) => {
    if (!groupId) return;
    setSelectedDay(dateStr);
    setDayLoading(true);
    api.get(`/calendar/day?group_id=${groupId}&date=${dateStr}`)
      .then(r => setDayTxns(r.data))
      .finally(() => setDayLoading(false));
  };

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
    setSelectedDay(null);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
    setSelectedDay(null);
  };

  const daysInMonth  = getDaysInMonth(year, month);
  const offset       = firstDayOffset(year, month);
  const maxDay       = data ? Math.max(...Object.values(data.days).map(d => d.total), 1) : 1;
  const todayStr     = now.toISOString().slice(0, 10);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-800">{T.cal.title}</h1>
        {user?.role === 'superadmin' && (
          <select value={groupId} onChange={e => { setGroupId(e.target.value); setSelectedDay(null); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:outline-none">
            <option value="">{T.common.selectGroupOption}</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        )}
      </div>

      {!groupId ? (
        <div className="text-center py-20 text-gray-400">{T.cal.chooseGroup}</div>
      ) : (
        <div className="flex gap-5 flex-col lg:flex-row">
          {/* Left: calendar */}
          <div className="flex-1 min-w-0">
            {/* Month nav */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <button onClick={prevMonth}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors">
                  ‹
                </button>
                <h2 className="text-lg font-bold text-gray-800">
                  {MIEZI[month - 1]} {year}
                </h2>
                <button onClick={nextMonth}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors">
                  ›
                </button>
              </div>

              {/* Day-of-week headers */}
              <div className="grid grid-cols-7 border-b border-gray-100">
                {SIKU.map(s => (
                  <div key={s} className="text-center py-2 text-xs font-semibold text-gray-400 uppercase">
                    {s}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              {loading ? (
                <div className="text-center py-16 text-gray-400">{T.common.loading}</div>
              ) : (
                <div className="grid grid-cols-7">
                  {/* Empty cells before first day */}
                  {Array.from({ length: offset }).map((_, i) => (
                    <div key={`e${i}`} className="h-16 border-b border-r border-gray-50" />
                  ))}

                  {/* Day cells */}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day     = i + 1;
                    const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                    const dayData = data?.days?.[dateStr];
                    const total   = dayData?.total || 0;
                    const count   = dayData?.count || 0;
                    const frac    = total / maxDay;
                    const isToday    = dateStr === todayStr;
                    const isSelected = dateStr === selectedDay;
                    const isFuture   = dateStr > todayStr;

                    return (
                      <button
                        key={day}
                        onClick={() => !isFuture && openDay(dateStr)}
                        disabled={isFuture}
                        className={`h-16 border-b border-r border-gray-100 p-1.5 flex flex-col items-start transition-all relative
                          ${isFuture ? 'opacity-30 cursor-default' : 'cursor-pointer hover:ring-2 hover:ring-green-400 hover:z-10'}
                          ${isSelected ? 'ring-2 ring-green-600 z-10' : ''}
                          ${total > 0 ? heatColor(frac) : 'bg-white hover:bg-gray-50'}
                        `}
                      >
                        <span className={`text-xs font-bold leading-none mb-1
                          ${isToday ? 'bg-green-700 text-white rounded-full w-5 h-5 flex items-center justify-center text-center' : ''}
                          ${total > 0 && !isToday ? 'text-inherit' : !isToday ? 'text-gray-400' : ''}
                        `}>
                          {day}
                        </span>
                        {total > 0 && (
                          <>
                            <span className="text-xs font-semibold leading-none">{fmtK(total)}</span>
                            {count > 1 && (
                              <span className="text-xs opacity-70 leading-none">{count} malipo</span>
                            )}
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Month summary */}
              {data && !loading && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
                  <div className="flex gap-4 text-sm">
                    <span className="text-gray-500">
                      {T.cal.monthTotal}: <strong className="text-green-700">{fmt(data.total_month)} TZS</strong>
                    </span>
                    <span className="text-gray-500">
                      {T.cal.daysWithPayments.replace('{n}', data.days_with_payments)}
                    </span>
                  </div>
                  {/* Heat legend */}
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <span>{T.cal.less}</span>
                    {[0.1, 0.3, 0.6, 0.9].map(f => (
                      <div key={f} className={`w-4 h-4 rounded-sm border ${heatColor(f)}`} />
                    ))}
                    <span>{T.cal.more}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right panel */}
          <div className="lg:w-72 space-y-4">
            {/* Selected day transactions */}
            {selectedDay ? (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <h3 className="font-semibold text-gray-800 text-sm">
                    {new Date(selectedDay + 'T12:00:00').toLocaleDateString('sw-TZ', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </h3>
                  {data?.days?.[selectedDay] && (
                    <p className="text-green-700 font-bold">{fmt(data.days[selectedDay].total)} TZS</p>
                  )}
                </div>
                {dayLoading ? (
                  <div className="text-center py-6 text-gray-400 text-sm">{T.common.loading}</div>
                ) : dayTxns.length === 0 ? (
                  <div className="text-center py-6 text-gray-400 text-sm">{T.cal.noPaymentsDay}</div>
                ) : (
                  <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                    {dayTxns.map(t => (
                      <div key={t.id} className="px-4 py-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-gray-800">{t.member_name}</p>
                            <p className="text-xs text-gray-400">{t.type}</p>
                          </div>
                          <span className={`text-sm font-bold ${t.type === 'Kiingilio' ? 'text-blue-600' : 'text-green-700'}`}>
                            {fmt(t.amount)}
                          </span>
                        </div>
                        {t.receipt && (
                          <p className="text-xs text-gray-300 font-mono mt-0.5">{t.receipt}</p>
                        )}
                      </div>
                    ))}
                    <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-600 flex justify-between">
                      <span>{T.cal.dayTotal}</span>
                      <span className="text-green-700">
                        {fmt(dayTxns.reduce((s, t) => s + parseFloat(t.amount), 0))} TZS
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-center text-gray-400 text-sm">
                {T.cal.noEvents}
              </div>
            )}

            {/* Top contributors this month */}
            {data?.top_contributors?.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-700 text-sm">🏆 {T.cal.topContributors}</h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {data.top_contributors.map((c, i) => (
                    <div key={c.member_phone} className="flex items-center gap-3 px-4 py-2.5">
                      <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0
                        ${i === 0 ? 'bg-yellow-100 text-yellow-700' : i === 1 ? 'bg-gray-100 text-gray-500' : 'bg-orange-50 text-orange-500'}`}>
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{c.member_name}</p>
                        <p className="text-xs text-gray-400">{c.count} malipo</p>
                      </div>
                      <span className="text-sm font-bold text-green-700 flex-shrink-0">{fmt(c.total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick month stats */}
            {data && !loading && (
              <div className="bg-green-700 rounded-xl p-4 text-white">
                <p className="text-green-200 text-xs font-semibold uppercase mb-2">{T.cal.summary} - {MIEZI[month-1]}</p>
                <div className="text-3xl font-bold mb-1">{fmt(data.total_month)}</div>
                <div className="text-green-200 text-sm">{T.cal.totalMonth}</div>
                <div className="mt-3 pt-3 border-t border-green-600 text-sm text-green-100">
                  {T.cal.daysWithPayments.replace('{n}', `${data.days_with_payments}/${daysInMonth}`)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
